'use client';

import { usePathname } from 'next/navigation';
import { Container } from '@/components/primitives/Container/Container';
import { IStandardContentPage, ISubPageNavigationModularBlock } from '@/.generated';
import { IBaseComponentProps, WithMetadata } from '@/lib/types';
import { getCSLPAttributes } from '@/utils/type-guards';
import { usePageReferenceUrl } from '@/lib/hooks/usePageReferenceUrl';
import { ButtonWrapper } from '@/helpers/Wrappers/ButtonWrapper/ButtonWrapper';
import { subPageNavigationVariants } from './SubPageNavigation.styles';

type SubPageNavigationProps = WithMetadata<ISubPageNavigationModularBlock> & IBaseComponentProps;

type NavItemData = NonNullable<ISubPageNavigationModularBlock['navigation_items']>[number];

// Normalises any href to its pathname for comparison against usePathname(). Deliberately avoids
// `typeof window` branching (e.g. window.location.origin as the URL base) — that resolves relative
// hrefs differently on the server vs. the client and causes a hydration mismatch on the active-tab
// className. Absolute URLs parse the same everywhere; relative hrefs never need an origin at all.
const extractPathname = (href: string): string => {
  if (href.startsWith('/')) return href.split('?')[0].split('#')[0];
  try {
    return new URL(href).pathname;
  } catch {
    return href;
  }
};

// Returns true when the given href matches the current page pathname. Anchor hrefs (containing a
// `#`) are section links, not distinct pages/tabs — every anchor on the current page would
// otherwise share its pathname and all light up as "active" at once, so they're never eligible.
const isActivePath = (href: string | undefined, currentPathname: string): boolean => {
  if (!href || href.includes('#')) return false;
  const itemPath = extractPathname(href);
  return currentPathname === itemPath;
};

// Resolves page_reference to a URL (falls back to link.href) and renders the link
const NavLinkItem = ({
  item,
  layout,
  currentPathname,
}: {
  item: NavItemData;
  layout: 'tab-layout' | 'link-layout';
  currentPathname: string;
}) => {
  // page_reference takes priority for internal pages; link.href used as fallback
  const href = usePageReferenceUrl(
    item.page_reference as IStandardContentPage[] | undefined,
    item.link?.href
  );
  const title = item.navigation_title || item.link?.title;
  const active = isActivePath(href, currentPathname);

  const isOpenInNewTab = item?.opens_in_new_tab;

  if (!href || !title) return null;

  const { link, chevron, listWrapper } = subPageNavigationVariants({ layout, isActive: active });

  return (
    <li className={listWrapper()}>
      <ButtonWrapper
        href={href}
        className={link()}
        clickName={title}
        clickLocation="SubPageNavigation"
        clickType="link"
        {...getCSLPAttributes(item.$?.navigation_title)}
        cta={{ opens_in_new_tab: isOpenInNewTab, has_font_awesome_icons: false }}
      >
        {title}
        {layout === 'link-layout' && (
          <i className={`fa-sharp fa-solid fa-angle-right ${chevron()}`} aria-hidden="true" />
        )}
      </ButtonWrapper>
    </li>
  );
};

/**
 * Sub Page Navigation — renders related-page links in two layout variants:
 *
 * - `tab-layout`: horizontal row of bordered button tabs (active tab has primary fill).
 *   On mobile items stack full-width. (Sub Page Navigation FSD)
 *
 * - `link-layout`: horizontal row of inline text links each with a trailing chevron.
 *   On mobile items stack full-width. (Associated Topics FSD)
 *
 * Each item resolves its URL from page_reference (internal CMS page) with link
 * as a fallback. Active state is derived by matching the resolved href to the current pathname.
 * Returns null when fewer than 2 navigation items are configured.
 */
export const SubPageNavigation = (props: SubPageNavigationProps) => {
  const {
    componentName,
    _metadata,
    inline_navigation_id,
    hide_component,
    layout,
    navigation_items,
    headline_text,
    $,
  } = props;
  const pathname = usePathname();

  if (hide_component) return null;

  if (!navigation_items || navigation_items.length < 2) return null;

  const resolvedLayout = layout ?? 'tab-layout';
  const { nav, list, subNavheader, container } = subPageNavigationVariants({
    layout: resolvedLayout,
  });

  return (
    <Container
      className={container()}
      componentName={componentName}
      metaId={_metadata?.uid}
      id={inline_navigation_id}
    >
      <nav aria-label="Sub page navigation" className={nav()} {...getCSLPAttributes($?.layout)}>
        {headline_text && layout === 'link-layout' && (
          <div className={subNavheader()}>{headline_text}</div>
        )}
        <ul className={list()}>
          {navigation_items.map((item, index) => (
            <NavLinkItem
              key={index}
              item={item}
              layout={resolvedLayout}
              currentPathname={pathname}
            />
          ))}
        </ul>
      </nav>
    </Container>
  );
};
